import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import '../../styles/chat.css';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 3000;
const SESSION_KEY = 'eliana_chat_session_id';

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const lastSeenRef = useRef(null);
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const ensureSession = useCallback(async () => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      const { data } = await axios.post(`${API}/api/chat/session`);
      sid = data.session_id;
      localStorage.setItem(SESSION_KEY, sid);
    }
    setSessionId(sid);
    return sid;
  }, []);

  const fetchMessages = useCallback(async (sid, after) => {
    try {
      const url = `${API}/api/chat/messages?session_id=${sid}${after ? `&after=${encodeURIComponent(after)}` : ''}`;
      const { data } = await axios.get(url);
      if (data.length === 0) return;
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const fresh = data.filter((m) => !known.has(m.id));
        if (fresh.length === 0) return prev;
        const latest = fresh[fresh.length - 1].created_at;
        lastSeenRef.current = latest;
        // Mark unread if widget closed and there's an owner reply
        if (!open && fresh.some((m) => m.sender === 'owner')) {
          setHasUnread(true);
        }
        return [...prev, ...fresh];
      });
    } catch (err) {
      // Session might have been deleted server-side; reset
      if (err?.response?.status === 404) {
        localStorage.removeItem(SESSION_KEY);
        setSessionId(null);
        setMessages([]);
      }
    }
  }, [open]);

  // Initialize session on mount (silently, so chat is ready when opened)
  useEffect(() => {
    ensureSession().then((sid) => {
      fetchMessages(sid, null);
    }).catch(() => { /* ignore */ });
  }, [ensureSession, fetchMessages]);

  // Polling loop
  useEffect(() => {
    if (!sessionId) return undefined;
    pollRef.current = setInterval(() => {
      fetchMessages(sessionId, lastSeenRef.current);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [sessionId, fetchMessages]);

  // Autoscroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Clear unread when opening
  useEffect(() => {
    if (open) setHasUnread(false);
  }, [open]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const sid = sessionId || (await ensureSession());
      const { data } = await axios.post(`${API}/api/chat/message`, {
        session_id: sid,
        text,
      });
      setMessages((prev) => [...prev.filter((m) => m.id !== data.id), data]);
      lastSeenRef.current = data.created_at;
      setInput('');
    } catch (err) {
      console.error('chat send failed', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        className={`chat-launcher ${open ? 'chat-launcher--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Cerrar chat' : 'Abrir chat'}
        data-testid="chat-launcher-btn"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && hasUnread && <span className="chat-launcher__dot" />}
        {!open && <span className="chat-launcher__label">Chatear</span>}
      </button>

      {/* Chat panel */}
      <div
        className={`chat-panel ${open ? 'chat-panel--open' : ''}`}
        role="dialog"
        aria-label="Chat con Eliana"
        data-testid="chat-panel"
      >
        <div className="chat-panel__header">
          <div className="chat-panel__avatar">E</div>
          <div className="chat-panel__title">
            <strong>Eliana Espilco</strong>
            <span>Respondemos lo antes posible</span>
          </div>
          <button
            type="button"
            className="chat-panel__close"
            onClick={() => setOpen(false)}
            aria-label="Cerrar"
            data-testid="chat-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div className="chat-panel__messages" ref={listRef} data-testid="chat-messages">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`chat-msg chat-msg--${m.sender}`}
              data-testid={`chat-msg-${m.sender}`}
            >
              <div className="chat-msg__bubble">{m.text}</div>
            </div>
          ))}
        </div>

        <form className="chat-panel__composer" onSubmit={handleSend}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe tu mensaje..."
            maxLength={2000}
            disabled={sending}
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            data-testid="chat-send-btn"
            aria-label="Enviar"
          >
            {sending ? <Loader2 size={18} className="chat-spin" /> : <Send size={18} />}
          </button>
        </form>
      </div>
    </>
  );
};

export default ChatWidget;
